import { request } from "umi";
import { paramsSerializer } from "../../api";

const urlPrefix = "/api/tenant/customer";

/**
 * Get Customer list
 *
 * GET api/jobs
 */
export async function getCustomerListByPage(params: API.PageParams & Partial<API.Dispatch>, sort?: any, filter?: any) {
    return request<API.ResultObject<API.PaginatedResult<API.Customer>>>(`${urlPrefix}/list`, {
        method: "GET",
        params: {
            ...params,
            perPage: params.pageSize,
            page: params.current,
            sort: sort && Object.keys(sort).length > 0 ? sort : { id: "descend" },
            filter,
        },
        withToken: true,
        paramsSerializer,
    }).then((res) => ({
        data: res.data?.rows ?? [],
        success: res.success ?? false,
        total: res.data?.pagination?.total_records ?? 0,
        pagination: res.data?.pagination,
        total_row: res.data?.total_row,
    }));
}

export async function addCustomer(data: Partial<API.Customer>) {
  return request<API.ResultObject<API.Customer>>(`${urlPrefix}/add`, {
    method: "POST",
    data,
    withToken: true,
    paramsSerializer,
  });
}

/** Get full customer by ID */
export async function getCustomer(params: { customer_id: number }) {
  return request<API.ResultObject<API.Customer>>(`/api/customers/tenants/get_customer`, {
    method: 'GET',
    params,
    withToken: true,
    paramsSerializer,
  }).then(res => res.data);
}

/** Add address (property) to customer */
export async function addCustomerAddress(data: { customer_id: number; street_number?: string; address: string; city?: string; state?: string; zip?: string; unit_number?: string; unit_type?: string }) {
  return request<API.ResultObject<API.CustomerProperty>>(`/api/customers/tenants/add-address`, {
    method: "POST",
    data,
    withToken: true,
    paramsSerializer,
  });
}

/** Update an existing address (property) of a customer */
export async function updateCustomerAddress(data: { property_id: number; street_number?: string; address: string; city?: string; state?: string; zip?: string; unit_number?: string; unit_type?: string }) {
  return request<API.ResultObject<API.CustomerProperty>>(`/api/customers/tenants/update-address`, {
    method: "POST",
    data,
    withToken: true,
    paramsSerializer,
  });
}

/** Pin a property's true location on the map (sets geolocation accuracy=manual) */
export async function updatePropertyGeolocation(data: { property_id: number; lat: number; lng: number }) {
  return request<API.ResultObject<API.CustomerPropertyGeolocation>>(`/api/customers/tenants/update-property-geolocation`, {
    method: "POST",
    data,
    withToken: true,
    paramsSerializer,
  });
}

/** Add email to customer */
export async function addCustomerEmail(data: { customer_id: number; email: string; email_type?: string; is_preferred?: boolean }) {
  return request<API.ResultObject<API.CustomerEmail>>(`/api/customers/tenants/add-email`, {
    method: "POST",
    data,
    withToken: true,
    paramsSerializer,
  });
}

/** Add phone to customer */
export async function addCustomerPhone(data: { customer_id: number; phone: string; phone_type?: string; is_preferred?: boolean }) {
  return request<API.ResultObject<API.CustomerPhone>>(`/api/customers/tenants/add-phone`, {
    method: "POST",
    data,
    withToken: true,
    paramsSerializer,
  });
}

/** Add email to customer (alias) */
export const addEmail = addCustomerEmail;

/** Add phone to customer (alias) */
export const addPhone = addCustomerPhone;

/** Set preferred email */
export async function setPreferredEmail(data: { email_id?: number; customer_id?: number; is_primary_email?: boolean }) {
  return request<API.ResultObject<API.CustomerEmail>>(`/api/customers/tenants/set-preferred-email`, {
    method: "POST",
    data,
    withToken: true,
    paramsSerializer,
  });
}

/** Unset preferred email */
export async function unsetPreferredEmail(data: { email_id?: number; customer_id?: number; is_primary_email?: boolean }) {
  return request<API.ResultObject<API.CustomerEmail>>(`/api/customers/tenants/unset-preferred-email`, {
    method: "POST",
    data,
    withToken: true,
    paramsSerializer,
  });
}

/** Set preferred phone */
export async function setPreferredPhone(data: { phone_id: number }) {
  return request<API.ResultObject<API.CustomerPhone>>(`/api/customers/tenants/set-preferred-phone`, {
    method: "POST",
    data,
    withToken: true,
    paramsSerializer,
  });
}

/** Unset preferred phone */
export async function unsetPreferredPhone(data: { phone_id: number }) {
  return request<API.ResultObject<API.CustomerPhone>>(`/api/customers/tenants/unset-preferred-phone`, {
    method: "POST",
    data,
    withToken: true,
    paramsSerializer,
  });
}

/** Delete customer email */
export async function deleteCustomerEmail(data: { email_id: number }) {
  return request<API.ResultObject<any>>(`/api/customers/tenants/delete-email`, {
    method: "POST",
    data,
    withToken: true,
    paramsSerializer,
  });
}

/** Delete customer phone */
export async function deleteCustomerPhone(data: { phone_id: number }) {
  return request<API.ResultObject<any>>(`/api/customers/tenants/delete-phone`, {
    method: "POST",
    data,
    withToken: true,
    paramsSerializer,
  });
}

/** Edit customer email */
export async function editCustomerEmail(data: { email_id: number; email: string; email_type?: string }) {
  return request<API.ResultObject<API.CustomerEmail>>(`/api/customers/tenants/edit-email`, {
    method: "POST",
    data,
    withToken: true,
    paramsSerializer,
  });
}

/** Edit customer phone */
export async function editCustomerPhone(data: { phone_id: number; phone: string; phone_type?: string }) {
  return request<API.ResultObject<API.CustomerPhone>>(`/api/customers/tenants/edit-phone`, {
    method: "POST",
    data,
    withToken: true,
    paramsSerializer,
  });
}

/** Update customer basic information */
export async function updateCustomer(data: { customer_id: number; first_name: string; last_name: string }) {
  return request<API.ResultObject<API.Customer>>(`/api/customers/tenants/update-customer`, {
    method: "POST",
    data,
    withToken: true,
    paramsSerializer,
  });
}

/** Update customer primary email */
export async function updateCustomerPrimaryEmail(data: { customer_id: number; email: string }) {
  return request<API.ResultObject<API.Customer>>(`/api/customers/tenants/update-primary-email`, {
    method: "POST",
    data,
    withToken: true,
    paramsSerializer,
  });
}

/** Update customer primary phone */
export async function updateCustomerPrimaryPhone(data: { customer_id: number; phone: string; phone_type?: string }) {
  return request<API.ResultObject<API.Customer>>(`/api/customers/tenants/update-primary-phone`, {
    method: "POST",
    data,
    withToken: true,
    paramsSerializer,
  });
}

/** List the automatic-SMS trigger kinds a customer can opt out of */
export async function getSmsTriggerOptions() {
  return request<API.ResultObject<{ value: string; label: string }[]>>(`/api/customers/tenants/sms-trigger-options`, {
    method: "GET",
    withToken: true,
    paramsSerializer,
  }).then((res) => res.data ?? []);
}

/** Update which automatic-SMS triggers are disabled for a customer, and/or whether manual SMS sends are allowed */
export async function updateCustomerSmsPreferences(data: { customer_id: number; disabled_sms_triggers: string[]; manual_sms_enabled?: boolean }) {
  return request<API.ResultObject<API.Customer>>(`/api/customers/tenants/update-sms-preferences`, {
    method: "POST",
    data,
    withToken: true,
    paramsSerializer,
  });
}

/** Update the customer's marketing-text consent and/or the "declined replacement discussion" flag */
export async function updateCustomerMarketingConsent(data: { customer_id: number; marketing_sms_consent?: boolean; replacement_discussion_declined?: boolean }) {
  return request<API.ResultObject<API.Customer>>(`/api/customers/tenants/update-marketing-consent`, {
    method: "POST",
    data,
    withToken: true,
    paramsSerializer,
  });
}

/** Update customer source */
export async function updateCustomerSource(data: { customer_id: number; source: string }) {
  return request<API.ResultObject<API.Customer>>(`/api/customers/tenants/update-source`, {
    method: "POST",
    data,
    withToken: true,
    paramsSerializer,
  });
}

export async function getCustomerDuplicateRules() {
  return request<API.ResultObject<API.CustomerDuplicateRule[]>>(`${urlPrefix}/duplicate-rules`, {
    method: "GET",
    withToken: true,
    paramsSerializer,
  }).then((res) => res.data);
}

export async function getCustomerDuplicateCandidates(params: {
  base_customer_ids: number[];
  rules: string[];
  match_mode?: "score" | "any" | "all";
  min_score?: number;
}) {
  return request<API.ResultObject<API.CustomerDuplicateCandidate[]>>(`${urlPrefix}/duplicate-candidates`, {
    method: "GET",
    params: { ...params, base_customer_ids: params.base_customer_ids.join(","), rules: params.rules.join(",") },
    withToken: true,
    paramsSerializer,
  }).then((res) => res.data);
}

export async function previewCustomerCanonicalMerge(data: { customer_ids: number[] }) {
  return request<API.ResultObject<API.CustomerMergePreview>>(`${urlPrefix}/merge-preview`, {
    method: "POST",
    data,
    withToken: true,
    paramsSerializer,
  }).then((res) => res.data);
}

export async function createCanonicalCustomerMerge(data: {
  customer_ids: number[];
  canonical: Partial<API.Customer> & { preferred_phone?: string; preferred_email?: string };
  merge_note?: string;
}) {
  return request<API.ResultObject<API.Customer>>(`${urlPrefix}/merge-create-canonical`, {
    method: "POST",
    data,
    withToken: true,
    paramsSerializer,
  });
}

/** Customer Snapshot (facets + OpenAI narrative). Computes facets on the spot when none exist yet. */
export async function getCustomerSnapshot(params: { customer_id: number }) {
  return request<API.ResultObject<API.CustomerSnapshot>>(`${urlPrefix}/snapshot`, {
    method: "GET",
    params,
    withToken: true,
    paramsSerializer,
  }).then((res) => res.data);
}

/** Recompute facets and ask OpenAI for a fresh narrative now (subject to key / daily cap). */
export async function regenerateCustomerSnapshot(data: { customer_id: number }) {
  return request<API.ResultObject<API.CustomerSnapshot>>(`${urlPrefix}/snapshot/regenerate`, {
    method: "POST",
    data,
    withToken: true,
    paramsSerializer,
  }).then((res) => res.data);
}
